import { blobToFile } from 'utils/imageCropUtils'

const DEFAULT_OPTIONS = {
  maxWidth: 1920,
  quality: 0.8,
  maxSizeBytes: 5 * 1024 * 1024,
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image'))
    }
    img.src = url
  })
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('Failed to compress image'))
      },
      type,
      quality
    )
  })
}

function replaceExtension(filename, ext) {
  const dotIndex = filename.lastIndexOf('.')
  const base = dotIndex > 0 ? filename.substring(0, dotIndex) : filename
  return `${base}.${ext}`
}

export async function compressImage(file, options = {}) {
  const { maxWidth, quality, maxSizeBytes } = { ...DEFAULT_OPTIONS, ...options }

  // GIF: skip (may be animated)
  if (file.type === 'image/gif') return file

  // Load image to check dimensions
  const img = await loadImage(file)

  // No compression needed: small file AND small dimensions
  if (file.size <= maxSizeBytes && img.width <= maxWidth) return file

  // Calculate scaled dimensions
  let width = img.width
  let height = img.height
  if (width > maxWidth) {
    height = Math.round(height * (maxWidth / width))
    width = maxWidth
  }

  // Draw to canvas
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Failed to get canvas context')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, 0, 0, width, height)

  // Compress with decreasing quality until under maxSizeBytes
  const outputFilename = replaceExtension(file.name, 'jpg')
  const startQ = Math.max(Math.round(quality * 10), 5)

  for (let q = startQ; q >= 5; q -= 1) {
    const currentQuality = q / 10
    const blob = await canvasToBlob(canvas, 'image/jpeg', currentQuality)
    if (blob.size <= maxSizeBytes || q === 5) {
      canvas.width = 0
      canvas.height = 0
      return blobToFile(blob, outputFilename)
    }
  }
}

export async function compressImages(files, options = {}) {
  const results = []
  for (const file of files) {
    results.push(await compressImage(file, options))
  }
  return results
}
